import { z } from 'zod';

import { validateJSONString } from '@nw/shared/utils';

export const ErrorsResponseSchema = z.object({
  error: z.string().optional(),
  message: z.string().optional(),
  status_code: z.number().optional(),
  res_status: z.string().optional(),
  http_status_code: z.number().optional(),
  response: z.string().optional()
});

export type ErrorResponseType = z.infer<typeof ErrorsResponseSchema>;

const FieldResponseGraphQlConfigSchema = z.object({
  field_id: z.string(),
  app_model_name: z.string(),
  model_field_name: z.string(),
  extra_config: z.string().nullable().optional()
});

export type FieldResponseGraphQlConfigType = z.infer<
  typeof FieldResponseGraphQlConfigSchema
>;

const DependedFieldConfigSchema = z.object({
  id: z.string(),
  opted_value: z.string()
});

export type DependedGroupConfigType = z.infer<typeof DependedFieldConfigSchema>;

const FieldConfigDetailsSchema = z.object({
  id: z.string(),
  type: z.enum([
    'PLAIN_TEXT',
    'PHONE_NUMBER',
    'EMAIL',
    'NUMBER',
    'LONG_TEXT',
    'DROPDOWN',
    'RADIO_GROUP',
    'CHECKBOX_GROUP',
    'MULTI_SELECT',
    'DATE',
    'FILE_UPLOADER',
    'COMMON_DATA_SEARCH',
    'CHECKBOX',
    'FLOAT',
    'CONTENT'
  ]),
  name: z.string(),
  label: z.string(),
  label_helper_text: z.string().nullable().optional(),
  placeholder_text: z.string().nullable().optional(),
  validation_config: z.string().nullable().optional(),
  helper_text: z.string().nullable().optional(),
  default_value: z.string().nullable().optional(),
  is_required: z.boolean(),
  extra_config: z.string().nullable().optional(),
  response_submission_type: z.enum([
    'OTHER_APP_WITH_GRAPHQL',
    'TEMPLATE_FIELD_RESPONSE',
    'NO_RESPONSE'
  ]),
  order: z.number(),
  field_response_graph_ql_config: FieldResponseGraphQlConfigSchema,
  depended_fields: z.array(DependedFieldConfigSchema)
});

export type FieldConfigDetailsType = z.infer<typeof FieldConfigDetailsSchema>;

const GroupConfigFieldSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  order: z.number(),
  display_type: z.enum(['BLOCK', 'IN_LINE']),
  fields: z.array(FieldConfigDetailsSchema)
});

export type GroupConfigFieldType = z.infer<typeof GroupConfigFieldSchema>;

const ProductRegistrationConfigDetailsSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  product_code: z.string(),
  product_registration_url: z.string()
});

export type ProductRegistrationConfigDetailsType = z.infer<
  typeof ProductRegistrationConfigDetailsSchema
>;

export const TemplateConfigDetailsSchema = z.object({
  template_id: z.string(),
  name: z.string(),
  extra_config: z.string().optional(),
  group_config_fields: z.array(GroupConfigFieldSchema)
});

export type TemplateConfigDetailsType = z.infer<
  typeof TemplateConfigDetailsSchema
>;

const FormSubmissionConfigDetailsSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  form_id: z.string(),
  form_submission_url: z.string()
});

export type FormSubmissionConfigDetailsType = z.infer<
  typeof FormSubmissionConfigDetailsSchema
>;

const SectionEntityConfigSchema = z.object({
  entity_type: z.enum([
    'TEMPLATE',
    'PRODUCT_REGISTRATION',
    'FORM',
    'TOPIN_EXAM_COMPLETED'
  ]),
  order: z.number(),
  extra_config: z.string().optional(),
  template_config_details: TemplateConfigDetailsSchema.optional(),
  product_registration_config_details:
    ProductRegistrationConfigDetailsSchema.optional(),
  form_submission_config_details: FormSubmissionConfigDetailsSchema.optional()
});

export type SectionEntityConfigType = z.infer<typeof SectionEntityConfigSchema>;

const UserTimeSpentConfigSchema = z.object({
  condition_config: z.object({
    min_value: z.number(),
    max_value: z.number(),
    value_type: z.enum(['SECONDS']),
    condition_type: z.enum(['RANGE'])
  }),
  discount_details: z.object({
    amount_in_least_sub_unit: z.number(),
    coupon_code: z.string()
  })
});

const ActionConfigSchema = z.object({
  action_type: z.enum(['ADD_DISCOUNT']),
  action_config: z.string().refine(
    validateJSONString(
      z.object({
        discount_allocation_type: z.enum(['USER_TIME_SPENT']),
        user_time_spent_config: z.array(UserTimeSpentConfigSchema)
      }),
      false
    )
  )
});

export type ActionConfigType = z.infer<typeof ActionConfigSchema>;

const SectionDetailsSchema = z.object({
  section_id: z.string().uuid(),
  title: z.string(),
  description: z.string(),
  name_enum: z.string(),
  order: z.number(),
  parent_section_config_id: z.string().uuid().optional(),
  section_completed_at: z.string().optional(),
  completion_percentage: z.number(),
  entities_details: z.array(SectionEntityConfigSchema),
  section_action_configs: z.array(ActionConfigSchema).optional(),
  first_visited_datetime: z.string().nullable().optional()
});

export type SectionDetailsType = z.infer<typeof SectionDetailsSchema>;

export const GetApplicationSectionDetailsResponseSchema =
  z.array(SectionDetailsSchema);

export type GetApplicationSectionDetailsResponseType = z.infer<
  typeof GetApplicationSectionDetailsResponseSchema
>;

export const IdentityEnums = z.enum(['PARENT', 'STUDENT']);

const UserApplicationDetailsSchema = z.object({
  user_application_id: z.string(),
  user_id: z.string(),
  identity: IdentityEnums
});

export type UserApplicationDetailsType = z.infer<
  typeof UserApplicationDetailsSchema
>;

export const GetUserApplicationDetailsResponseSchema = z.object({
  application_id: z.string(),
  application_number: z.string(),
  meta_data: z.string().nullable().optional(),
  user_application_details: z.array(UserApplicationDetailsSchema)
});

export type GetUserApplicationDetailsResponseType = z.infer<
  typeof GetUserApplicationDetailsResponseSchema
>;

export const GetUserApplicationDetailsRequestObjectSchema = z.object({
  metadata: z.string().optional()
});

export type GetUserApplicationDetailsRequestObjectType = z.infer<
  typeof GetUserApplicationDetailsRequestObjectSchema
>;

export const CreateUserApplicationRequestObjectSchema = z.object({
  application_name_enum: z.string(),
  identity: IdentityEnums.optional(),
  metadata: z.string().optional()
});

export type CreateUserApplicationRequestObjectType = z.infer<
  typeof CreateUserApplicationRequestObjectSchema
>;

export const CreateUserApplicationResponseSchema = z.object({
  application_id: z.string(),
  application_number: z.string()
});

export type CreateUserApplicationResponseType = z.infer<
  typeof CreateUserApplicationResponseSchema
>;

export const UpdateUserApplicationRequestObjectSchema = z.object({
  application_id: z.string(),
  identity: IdentityEnums.optional(),
  metadata: z.string().optional()
});

export type UpdateUserApplicationRequestObjectType = z.infer<
  typeof UpdateUserApplicationRequestObjectSchema
>;

export const UpdateUserApplicationResponseSchema = z.object({
  application_id: z.string(),
  application_number: z.string()
});

export type UpdateUserApplicationResponseType = z.infer<
  typeof UpdateUserApplicationResponseSchema
>;

export const UpdateMultipleTemplateResponseRequestObjectSchema = z.object({
  application_id: z.string(),
  template_id: z.string(),
  section_id: z.string(),
  field_responses: z.array(
    z.object({
      field_id: z.string(),
      field_value: z.string()
    })
  )
});

export type UpdateMultipleTemplateResponseRequestObjectType = z.infer<
  typeof UpdateMultipleTemplateResponseRequestObjectSchema
>;

export const UpdateMultipleTemplateResponseResponseSchema = z.object({
  completion_percentage: z.number()
});

export type UpdateMultipleTemplateResponseResponseType = z.infer<
  typeof UpdateMultipleTemplateResponseResponseSchema
>;

export const LinkUserApplicationRequestObjectSchema = z.object({
  user_id: z.string(),
  application_id: z.string(),
  identity: IdentityEnums.optional()
});

export type LinkUserApplicationRequestObjectType = z.infer<
  typeof LinkUserApplicationRequestObjectSchema
>;

export const GetUserIdentityAccessTokenRequestObjectSchema = z.object({
  user_id: z.string(),
  application_id: z.string()
});

export type GetUserIdentityAccessTokenRequestObjectType = z.infer<
  typeof GetUserIdentityAccessTokenRequestObjectSchema
>;

export const GetUserIdentityAccessTokenResponseSchema = z.object({
  access_token: z.string(),
  user_id: z.string(),
  refresh_token: z.string(),
  expires_in: z.number()
});

export type GetUserIdentityAccessTokenResponseType = z.infer<
  typeof GetUserIdentityAccessTokenResponseSchema
>;

export const UpdateTemplateResponseRequestObjectSchema = z.object({
  application_id: z.string(),
  template_id: z.string(),
  section_id: z.string(),
  field_id: z.string(),
  field_value: z.string()
});

export type UpdateTemplateResponseRequestObjectType = z.infer<
  typeof UpdateTemplateResponseRequestObjectSchema
>;

export const UpdateTemplateResponseResponseSchema = z.object({
  completion_percentage: z.number()
});

export type UpdateTemplateResponseResponseType = z.infer<
  typeof UpdateTemplateResponseResponseSchema
>;

export const GetTemplateResponseRequestObjectSchema = z.object({
  application_id: z.string(),
  template_id: z.string()
});

export type GetTemplateResponseRequestObjectType = z.infer<
  typeof GetTemplateResponseRequestObjectSchema
>;

export const GetTemplateResponseResponseSchema = z.object({
  field_responses: z.array(
    z.object({
      field_id: z.string(),
      field_value: z.string()
    })
  )
});

export type GetTemplateResponseResponseType = z.infer<
  typeof GetTemplateResponseResponseSchema
>;

export const GetOrCreateApplicationSectionVisitRequestObjectSchema = z.object({
  application_id: z.string().uuid(),
  section_id: z.string().uuid()
});

export type GetOrCreateApplicationSectionVisitRequestObjectType = z.infer<
  typeof GetOrCreateApplicationSectionVisitRequestObjectSchema
>;

export const GetOrCreateApplicationSectionVisitResponseSchema = z.object({
  first_visited_datetime: z.string(),
  current_datetime: z.string()
});

export type GetOrCreateApplicationSectionVisitResponseType = z.infer<
  typeof GetOrCreateApplicationSectionVisitResponseSchema
>;

export const ExecuteSectionActionRequestObjectSchema = z.object({
  action_type: z.enum(['ADD_DISCOUNT']),
  section_id: z.string().uuid()
});

export type ExecuteSectionActionRequestObjectType = z.infer<
  typeof ExecuteSectionActionRequestObjectSchema
>;

export const CardDetailsDescriptionSchema = z.object({
  self: z.string(),
  sharable: z.string()
});

export const CardDetailsSchema = z.object({
  title: z.string(),
  description: CardDetailsDescriptionSchema,
  highlighted_text: z.string(),
  icon_url: z.string()
});

export type CardDetailsType = z.infer<typeof CardDetailsSchema>;

export const AssessmentParameterScoreDetailSchema = z.object({
  name_enum: z.string(),
  name: z.string(),
  score: z.number(),
  priority: z.number(),
  score_range: z.enum(['LOW', 'MODERATE', 'HIGH']),
  card_details: CardDetailsSchema
});

export type AssessmentParameterScoreDetailType = z.infer<
  typeof AssessmentParameterScoreDetailSchema
>;

export const GetUserAssessmentReportResponseSchema = z.object({
  assessment_parameters_score_details: z.array(
    AssessmentParameterScoreDetailSchema
  ),
  user_details: z.object({
    name: z.string()
  })
});

export type GetUserAssessmentReportResponseType = z.infer<
  typeof GetUserAssessmentReportResponseSchema
>;

export const GetUserAssessmentReportRequestObjectSchema = z.object({
  section_config_id: z.string(),
  user_id: z.string()
});

export type GetUserAssessmentReportRequestObjectType = z.infer<
  typeof GetUserAssessmentReportRequestObjectSchema
>;

export const DiscountInteractionStateEnum = z.enum([
  'NO_INTERACTION',
  'COPIED',
  'DISMISSED'
]);
export type DiscountInteractionStateType = z.infer<
  typeof DiscountInteractionStateEnum
>;

export const DiscountDataSchema = z.object({
  coupon_code: z.string(),
  discount_value: z.number()
});
export type DiscountDataType = z.infer<typeof DiscountDataSchema>;

export const GetApplicationDiscountDetailsRequestObjectSchema = z.object({
  application_id: z.string()
});
export type GetApplicationDiscountDetailsRequestObjectType = z.infer<
  typeof GetApplicationDiscountDetailsRequestObjectSchema
>;

export const GetApplicationDiscountDetailsResponseSchema = z.object({
  interaction_state: DiscountInteractionStateEnum.nullable(),
  coupon_data: DiscountDataSchema.nullable()
});
export type GetApplicationDiscountDetailsResponseType = z.infer<
  typeof GetApplicationDiscountDetailsResponseSchema
>;

export const RecordDiscountInteractionRequestObjectSchema = z.object({
  application_id: z.string(),
  interaction_type: z.enum(['COPIED', 'DISMISSED'])
});
export type RecordDiscountInteractionRequestObjectType = z.infer<
  typeof RecordDiscountInteractionRequestObjectSchema
>;

export const RecordDiscountInteractionResponseSchema = z.object({
  coupon_code: z.string()
});
export type RecordDiscountInteractionResponseType = z.infer<
  typeof RecordDiscountInteractionResponseSchema
>;
